import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, KeyboardAvoidingView, Platform, InputAccessoryView } from 'react-native';
import { Image } from 'expo-image';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useNotes } from '@/contexts/NotesContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSentimento } from '@/contexts/SentimentoContext';
import { useLanguage } from '@/contexts/LanguageContext';
import FormattedText from '@/components/FormattedText';

interface TextFormat {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
}

interface FormattedSegment {
  text: string;
  formats: TextFormat;
  start: number;
  end: number;
}

const INPUT_ACCESSORY_VIEW_ID = 'uniqueID';

export default function AddNoteModal() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const [newNoteText, setNewNoteText] = useState('');
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [textSegments, setTextSegments] = useState<FormattedSegment[]>([]);
  const textInputRef = useRef<TextInput>(null);
  const { addNote, updateNote, userNotes } = useNotes();
  const { updateEntry } = useSentimento();
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const editNoteId = params.editNoteId as string;
  const editSentimentoId = params.editSentimentoId as string;
  const isEditing = !!editNoteId;
  const isEditingSentimento = !!editSentimentoId;

  useEffect(() => {
    if (isEditing && editNoteId) {
      // Find the note to edit
      const noteToEdit = userNotes.find(note => note.id === editNoteId);
      if (noteToEdit) {
        const { plainText, segments } = parseHTMLToSegments(noteToEdit.content);
        setNewNoteText(plainText);
        setTextSegments(segments);
      } else if (params.noteContent) {
        // Fallback to params if note not found in context
        const { plainText, segments } = parseHTMLToSegments(params.noteContent as string);
        setNewNoteText(plainText);
        setTextSegments(segments);
      }
    }
  }, [isEditing, editNoteId, userNotes, params.noteContent]);

  const parseHTMLToSegments = (html: string) => {
    // Simple HTML parser that works in React Native
    let plainText = '';
    const segments: FormattedSegment[] = [];

    // If it's just plain text without HTML tags
    if (!html.includes('<')) {
      plainText = html;
      return { plainText, segments };
    }

    // Simple regex-based HTML parser for React Native
    let currentPos = 0;
    let currentFormats: TextFormat = { bold: false, italic: false, underline: false, strikethrough: false };
    const formatStack: TextFormat[] = [];

    // Split by HTML tags while preserving them
    const parts = html.split(/(<\/?[^>]+>)/);

    for (const part of parts) {
      if (part.startsWith('<')) {
        // Handle HTML tags
        const isClosing = part.startsWith('</');
        const tagName = part.replace(/<\/?([^>]+)>/, '$1').toLowerCase();

        if (isClosing) {
          // Restore previous format state
          if (formatStack.length > 0) {
            currentFormats = formatStack.pop() || { bold: false, italic: false, underline: false, strikethrough: false };
          }
        } else {
          // Push current format state to stack
          formatStack.push({ ...currentFormats });

          // Apply new formatting
          switch (tagName) {
            case 'b':
            case 'strong':
              currentFormats.bold = true;
              break;
            case 'i':
            case 'em':
              currentFormats.italic = true;
              break;
            case 'u':
              currentFormats.underline = true;
              break;
            case 's':
            case 'strike':
              currentFormats.strikethrough = true;
              break;
          }
        }
      } else if (part.length > 0) {
        // Handle text content
        const start = currentPos;
        const end = currentPos + part.length;

        // Only add segment if it has formatting
        if (currentFormats.bold || currentFormats.italic || currentFormats.underline || currentFormats.strikethrough) {
          segments.push({
            text: part,
            formats: { ...currentFormats },
            start,
            end
          });
        }

        plainText += part;
        currentPos += part.length;
      }
    }

    return { plainText, segments };
  };

  const handleSaveNote = async () => {
    if (newNoteText.trim()) {
      const formattedText = formatTextForSaving();
      try {
        if (isEditingSentimento && editSentimentoId) {
          // Update sentiment entry note
          const updatedEntry = await updateEntry(editSentimentoId, {
            noteContent: formattedText
          });
          console.log('Updating sentiment note:', formattedText);
          if (updatedEntry) {
            // PRD §5.4 — `diary_note_submitted` (sentiment note edit path).
            import('@/utils/clarityEvents')
              .then(({ logDiaryNoteSubmitted, lengthBucket }) =>
                logDiaryNoteSubmitted({ length_bucket: lengthBucket(formattedText.length) })
              )
              .catch(() => {});
            router.back();
          } else {
            console.error('Failed to update sentiment entry');
          }
        } else if (isEditing && editNoteId) {
          // Update existing regular note
          await updateNote(editNoteId, formattedText);
          console.log('Updating note:', formattedText);
          // PRD §5.4 — `diary_note_submitted` (regular note edit path).
          import('@/utils/clarityEvents')
            .then(({ logDiaryNoteSubmitted, lengthBucket }) =>
              logDiaryNoteSubmitted({ length_bucket: lengthBucket(formattedText.length) })
            )
            .catch(() => {});
          router.back();
        } else {
          // Add new note
          await addNote(formattedText);
          console.log('Saving note:', formattedText);
          // PRD §5.4 — `diary_note_submitted` (new-note creation path — the
          // most common call site). All three branches fire the same event
          // because the dashboard cares about note volume, not whether it
          // was a new entry vs an edit. `length_bucket` keeps the metric
          // PII-free while still surfacing depth-of-engagement.
          import('@/utils/clarityEvents')
            .then(({ logDiaryNoteSubmitted, lengthBucket }) =>
              logDiaryNoteSubmitted({ length_bucket: lengthBucket(formattedText.length) })
            )
            .catch(() => {});
          router.replace({
            pathname: '/diary',
            params: { noteSaved: 'true' }
          });
        }
      } catch (error) {
        console.error('Error saving note:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleCloseModal = () => {
    router.back();
  };

  const applyFormatting = (formatType: keyof TextFormat) => {
    const { start, end } = selection;

    // Only apply formatting if text is selected
    if (start === end) {
      return; // No selection, do nothing
    }

    const selectedText = newNoteText.substring(start, end);

    // Check if there's already a segment with this exact range
    const existingSegmentIndex = textSegments.findIndex(
      segment => segment.start === start && segment.end === end
    );

    if (existingSegmentIndex !== -1) {
      // Modify existing segment
      const updatedSegments = [...textSegments];
      updatedSegments[existingSegmentIndex] = {
        ...updatedSegments[existingSegmentIndex],
        formats: {
          ...updatedSegments[existingSegmentIndex].formats,
          [formatType]: !updatedSegments[existingSegmentIndex].formats[formatType]
        }
      };
      setTextSegments(updatedSegments);
    } else {
      // Create new segment or merge with overlapping ones
      const newSegment: FormattedSegment = {
        text: selectedText,
        formats: {
          bold: formatType === 'bold',
          italic: formatType === 'italic',
          underline: formatType === 'underline',
          strikethrough: formatType === 'strikethrough'
        },
        start,
        end
      };

      // Remove overlapping segments and add new one
      const filteredSegments = textSegments.filter(segment => 
        segment.end <= start || segment.start >= end
      );

      setTextSegments([...filteredSegments, newSegment].sort((a, b) => a.start - b.start));
    }
  };

  const getFormatForSelection = () => {
    const { start, end } = selection;

    if (start === end) {
      return { bold: false, italic: false, underline: false, strikethrough: false };
    }

    // Find segment that exactly matches selection
    const matchingSegment = textSegments.find(
      segment => segment.start === start && segment.end === end
    );

    if (matchingSegment) {
      return matchingSegment.formats;
    }

    return { bold: false, italic: false, underline: false, strikethrough: false };
  };

  const handleTextChange = (text: string) => {
    const lengthDiff = text.length - newNoteText.length;
    const changePosition = selection.start;

    // Update text
    setNewNoteText(text);

    // Adjust segment positions based on text changes
    if (lengthDiff !== 0) {
      const updatedSegments = textSegments.map(segment => {
        if (segment.start >= changePosition) {
          return {
            ...segment,
            start: segment.start + lengthDiff,
            end: segment.end + lengthDiff,
            text: text.substring(segment.start + lengthDiff, segment.end + lengthDiff)
          };
        } else if (segment.end > changePosition) {
          // Segment spans across the change point
          const newEnd = segment.end + lengthDiff;
          if (newEnd <= segment.start) {
            // Segment became invalid, remove it
            return null;
          }
          return {
            ...segment,
            end: newEnd,
            text: text.substring(segment.start, newEnd)
          };
        }
        return segment;
      }).filter(segment => segment !== null) as FormattedSegment[];

      setTextSegments(updatedSegments);
    }
  };

  const handleSelectionChange = (event: any) => {
    const { start, end } = event.nativeEvent.selection;
    setSelection({ start, end });
  };

  const formatTextForSaving = () => {
    if (textSegments.length === 0) {
      return newNoteText;
    }

    // Sort segments by start position
    const sortedSegments = [...textSegments].sort((a, b) => a.start - b.start);

    let result = '';
    let lastEnd = 0;

    sortedSegments.forEach(segment => {
      // Add unformatted text before this segment
      if (segment.start > lastEnd) {
        result += newNoteText.substring(lastEnd, segment.start);
      }

      // Add formatted segment
      let segmentText = newNoteText.substring(segment.start, segment.end);

      if (segment.formats.bold) {
        segmentText = `<b>${segmentText}</b>`;
      }
      if (segment.formats.italic) {
        segmentText = `<i>${segmentText}</i>`;
      }
      if (segment.formats.underline) {
        segmentText = `<u>${segmentText}</u>`;
      }
      if (segment.formats.strikethrough) {
        segmentText = `<s>${segmentText}</s>`;
      }

      result += segmentText;
      lastEnd = segment.end;
    });

    // Add remaining unformatted text
    if (lastEnd < newNoteText.length) {
      result += newNoteText.substring(lastEnd);
    }

    return result;
  };

  const currentFormat = getFormatForSelection();

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        {/* Modal Header */}
        <View style={styles.modalHeader}>
          <View style={styles.headerContent}>
            <TouchableOpacity onPress={handleCloseModal} style={styles.cancelButton}>
              <Text style={styles.cancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>
              {isEditingSentimento ? t('diary.addNote.titleEditEntry') : isEditing ? t('diary.addNote.titleEdit') : t('diary.addNote.titleNew')}
            </Text>
            <TouchableOpacity 
              style={[styles.saveHeaderButton, !newNoteText.trim() && styles.disabledSaveButton]}
              onPress={handleSaveNote}
              disabled={!newNoteText.trim()}
            >
              <Text style={styles.saveHeaderButtonText}>{t('common.save')}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Content Section */}
        <View style={styles.contentSection}>
          {/* User Info */}
          <View style={styles.userInfo}>
            <View style={styles.userGroup}>
              <View style={styles.userAvatar}>
                {userProfile?.avatar_url ? (
                  <Image 
                    source={{ uri: userProfile.avatar_url }} 
                    style={styles.avatarImage}
                  />
                ) : (
                  <View style={styles.avatarContent}>
                    <Svg width="20" height="20" viewBox="0 0 25 25" fill="none">
                      <Path fillRule="evenodd" clipRule="evenodd" d="M12.4849 15.8462C8.61731 15.8462 5.31445 16.431 5.31445 18.7729C5.31445 21.1148 8.59636 21.7205 12.4849 21.7205C16.3525 21.7205 19.6545 21.1348 19.6545 18.7938C19.6545 16.4529 16.3735 15.8462 12.4849 15.8462Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path fillRule="evenodd" clipRule="evenodd" d="M12.4849 12.5059C15.023 12.5059 17.0802 10.4478 17.0802 7.90969C17.0802 5.3716 15.023 3.31445 12.4849 3.31445C9.94682 3.31445 7.8887 5.3716 7.8887 7.90969C7.88015 10.4392 9.92396 12.4973 12.4525 12.5059H12.4849Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                  </View>
                )}
              </View>
              <Text style={styles.userName}>{userProfile?.full_name?.split(' ')[0] || t('diary.addNote.userFallback')}</Text>
            </View>
          </View>

          {/* Rich Text Editor - Overlaid TextInput with Formatted Text */}
          <View style={styles.textInputContainer}>
            <View style={styles.richTextEditor}>
              {/* Formatted Text Display - shows the formatted version */}
              <View style={styles.formattedTextContainer}>
                <FormattedText 
                  content={formatTextForSaving()} 
                  style={styles.formattedDisplayText}
                />
              </View>

              {/* Transparent TextInput overlaid on top */}
              <TextInput
                ref={textInputRef}
                style={[
                  styles.transparentTextInput,
                  newNoteText.trim() ? { color: 'transparent' } : { color: '#151515' }
                ]}
                placeholder={t('diary.addNote.placeholder')}
                value={newNoteText}
                onChangeText={handleTextChange}
                onSelectionChange={handleSelectionChange}
                multiline
                textAlignVertical="top"
                autoFocus
                placeholderTextColor="#A1A9B2"
                inputAccessoryViewID={Platform.OS === 'ios' ? INPUT_ACCESSORY_VIEW_ID : undefined}
                selectionColor="rgba(1, 84, 128, 0.3)"
              />
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>

      {/* Formatting Toolbar */}
      {Platform.OS === 'ios' && (
        <InputAccessoryView nativeID={INPUT_ACCESSORY_VIEW_ID}>
          <View style={styles.formattingToolbar}>
            <TouchableOpacity 
              style={[styles.formatButton, currentFormat.bold && styles.activeFormatButton]}
              onPress={() => applyFormatting('bold')}
              disabled={selection.start === selection.end}
            >
              <Text style={[styles.formatButtonText, styles.boldText, currentFormat.bold && styles.activeFormatText]}>B</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.formatButton, currentFormat.italic && styles.activeFormatButton]}
              onPress={() => applyFormatting('italic')}
              disabled={selection.start === selection.end}
            >
              <Text style={[styles.formatButtonText, styles.italicText, currentFormat.italic && styles.activeFormatText]}>I</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.formatButton, currentFormat.underline && styles.activeFormatButton]}
              onPress={() => applyFormatting('underline')}
              disabled={selection.start === selection.end}
            >
              <Text style={[styles.formatButtonText, styles.underlineText, currentFormat.underline && styles.activeFormatText]}>U</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.formatButton, currentFormat.strikethrough && styles.activeFormatButton]}
              onPress={() => applyFormatting('strikethrough')}
              disabled={selection.start === selection.end}
            >
              <Text style={[styles.formatButtonText, styles.strikethroughText, currentFormat.strikethrough && styles.activeFormatText]}>S</Text>
            </TouchableOpacity>
          </View>
        </InputAccessoryView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  modalHeader: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E7EAED',
    height: 56,
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelButton: {
    padding: 4,
  },
  cancelText: {
    color: '#757C85',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 14,
  },
  modalTitle: {
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    lineHeight: 14,
    textAlign: 'center',
    flex: 1,
  },
  saveHeaderButton: {
    padding: 4,
  },
  saveHeaderButtonText: {
    color: '#015480',
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    lineHeight: 14,
  },
  disabledSaveButton: {
    opacity: 0.5,
  },
  contentSection: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 8,
  },
  userInfo: {
    width: '100%',
    marginBottom: 24,
  },
  userGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  userAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#DAD8CF',
    borderWidth: 1,
    borderColor: '#A19F97',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarContent: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  userName: {
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    lineHeight: 19.6,
  },
  textInputContainer: {
    flex: 1,
  },
  richTextEditor: {
    flex: 1,
    position: 'relative',
    minHeight: 200,
  },
  formattedTextContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingVertical: 0,
    paddingHorizontal: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },
  formattedDisplayText: {
    color: '#151515',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    textAlignVertical: 'top',
  },
  transparentTextInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    textAlignVertical: 'top',
    zIndex: 2,
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  formattingToolbar: {
    flexDirection: 'row',
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#E7EAED',
  },
  formatButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  activeFormatButton: {
    backgroundColor: '#F5F5F5',
  },
  formatButtonText: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
  },
  activeFormatText: {
    color: '#015480',
  },
  boldText: {
    fontWeight: 'bold',
  },
  italicText: {
    fontStyle: 'italic',
  },
  underlineText: {
    textDecorationLine: 'underline',
  },
  strikethroughText: {
    textDecorationLine: 'line-through',
  },
});